import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ClipboardCheck, Plus, Save } from "lucide-react";
import { qualificationFieldsApi, type QualificationFieldDto } from "@/api/qualificationFields";
import { Badge, Button, Input, Label } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";

/** Broker CRM Polish §3.8 — admin-editable lookup for the qualification
 *  checkboxes. Rows are deactivated (never deleted) so existing qualification
 *  selections keep rendering with their original label. */
export function QualificationFieldsAdminPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["qualification-fields-admin"],
    queryFn: () => qualificationFieldsApi.all(),
  });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["qualification-fields-admin"] });
    void qc.invalidateQueries({ queryKey: ["qualification-fields-active"] });
  };

  const create = useMutation({
    mutationFn: (label: string) =>
      qualificationFieldsApi.create({
        label,
        displayOrder: (list.data?.length ?? 0) + 1,
        isActive: true,
      }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: (p: { id: string; label: string; displayOrder: number; isActive: boolean }) =>
      qualificationFieldsApi.update(p.id, {
        label: p.label,
        displayOrder: p.displayOrder,
        isActive: p.isActive,
      }),
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) => qualificationFieldsApi.reorder(orderedIds),
    onSuccess: invalidate,
  });

  const move = (id: string, delta: -1 | 1) => {
    const rows = list.data ?? [];
    const idx = rows.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const swap = idx + delta;
    if (swap < 0 || swap >= rows.length) return;
    const next = rows.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    reorder.mutate(next.map((r) => r.id));
  };

  const [newLabel, setNewLabel] = useState("");

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Qualification fields"
        description="Checkboxes that appear on the Submit Qualification form. Deactivating preserves historical data — prefer it over deleting."
      />

      <div className="max-w-2xl grid gap-space-4">
        <form
          className="border border-border-light rounded-md p-space-3 bg-white-pure"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newLabel.trim()) return;
            create.mutate(newLabel.trim());
            setNewLabel("");
          }}
        >
          <Label className="mb-space-1 block">Add checkbox</Label>
          <div className="flex gap-space-2">
            <Input
              placeholder="e.g. Completed SBA training"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={!newLabel.trim() || create.isPending}>
              <Plus size={14} strokeWidth={1.5} aria-hidden /> Add
            </Button>
          </div>
        </form>

        {list.isLoading && (
          <div className="text-caption text-gray-mid">Loading…</div>
        )}

        {(list.data ?? []).length === 0 && !list.isLoading && (
          <div className="text-caption text-gray-mid italic">
            No fields yet. Add one above.
          </div>
        )}

        <ul className="grid gap-space-2">
          {(list.data ?? []).map((f, idx) => (
            <QualificationFieldRow
              key={f.id}
              field={f}
              canMoveUp={idx > 0}
              canMoveDown={idx < (list.data ?? []).length - 1}
              onMoveUp={() => move(f.id, -1)}
              onMoveDown={() => move(f.id, 1)}
              onSave={(label, displayOrder, isActive) =>
                update.mutateAsync({ id: f.id, label, displayOrder, isActive })
              }
            />
          ))}
        </ul>
      </div>
    </>
  );
}

function QualificationFieldRow({
  field,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onSave,
}: {
  field: QualificationFieldDto;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSave: (label: string, displayOrder: number, isActive: boolean) => Promise<QualificationFieldDto>;
}) {
  const [label, setLabel] = useState(field.label);
  const [isActive, setIsActive] = useState(field.isActive);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const dirty = label !== field.label || isActive !== field.isActive;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(label.trim(), field.displayOrder, isActive);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = () => {
    // Surface a warning before deactivating — historical qualifications
    // still render by label, but the field disappears from new forms.
    if (isActive) {
      setConfirmDeactivate(true);
      return;
    }
    setIsActive(true);
  };

  return (
    <li className={
      "flex items-center gap-space-2 border rounded-md p-space-3 bg-white-pure " +
      (isActive ? "border-border-light" : "border-border-light opacity-60")
    }>
      <ClipboardCheck size={14} strokeWidth={1.5} aria-hidden className="text-gold-shadow" />
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="flex-1"
      />
      <Badge status={isActive ? "finalized" : "archived"}>
        {isActive ? "Active" : "Inactive"}
      </Badge>
      <Button type="button" variant="text" size="sm" disabled={!canMoveUp} onClick={onMoveUp} aria-label="Move up">
        <ArrowUp size={14} strokeWidth={1.5} aria-hidden />
      </Button>
      <Button type="button" variant="text" size="sm" disabled={!canMoveDown} onClick={onMoveDown} aria-label="Move down">
        <ArrowDown size={14} strokeWidth={1.5} aria-hidden />
      </Button>
      <Button type="button" variant="secondary" size="sm" onClick={toggleActive}>
        {isActive ? "Deactivate" : "Activate"}
      </Button>
      <Button type="button" size="sm" onClick={save} disabled={!dirty || saving || !label.trim()}>
        <Save size={14} strokeWidth={1.5} aria-hidden />
        {saving ? "Saving…" : "Save"}
      </Button>

      {confirmDeactivate && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-40 bg-onyx/50 flex items-center justify-center p-space-4"
          onClick={() => setConfirmDeactivate(false)}
        >
          <div
            className="bg-white-pure rounded-lg shadow-lg max-w-sm w-full p-space-5 border-t-[2px] border-t-gold"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display font-bold text-h4 text-onyx m-0 mb-space-2">Deactivate field?</h3>
            <p className="text-small text-gray-dark">
              Deactivating will hide this checkbox from new qualifications. Existing data is preserved.
            </p>
            <div className="flex items-center justify-end gap-space-2 mt-space-3">
              <Button type="button" variant="text" size="sm" onClick={() => setConfirmDeactivate(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => { setIsActive(false); setConfirmDeactivate(false); }}
              >
                Deactivate
              </Button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
